import { isWaybillArchived } from "helpers/waybills";
import React, { useRef, useState } from "react";
import { applyArchiveStatus } from "sources/waybills";

interface WaybillArchiveActionProps {
    waybillId: number;
    intStateFlags: number;
    canArchive: boolean;
    modal: (params: {}) => void;
    operation: ReturnType<typeof useWaybillArchiveOperation>;
}

/** Keep request locks across collapsed/detail views until the server reload finishes. */
export const useWaybillArchiveOperation = (reload: () => void | Promise<unknown>) => {
    const [pendingIds, setPendingIds] = useState<number[]>([]);
    const requestPending = useRef(new Set<number>());

    const changeStatus = async (waybillId: number, archive: boolean) => {
        if (requestPending.current.has(waybillId)) {
            return false;
        }
        requestPending.current.add(waybillId);
        setPendingIds((ids) => [...ids, waybillId]);
        try {
            const success = await applyArchiveStatus({ waybillId, archive });
            if (success) {
                await reload();
            }
            return success;
        } catch (_error) {
            // The request service displays connection errors.
            return false;
        } finally {
            requestPending.current.delete(waybillId);
            setPendingIds((ids) => ids.filter((id) => id !== waybillId));
        }
    };

    return { pendingIds, changeStatus };
};

/** Confirm archive moves and allow restoring without an additional confirmation. */
export const WaybillArchiveAction = ({
    waybillId,
    intStateFlags,
    canArchive,
    modal,
    operation,
}: WaybillArchiveActionProps) => {
    const archived = isWaybillArchived(intStateFlags);
    const pending = operation.pendingIds.includes(waybillId);

    const handleClick = () => {
        if (archived) {
            return operation.changeStatus(waybillId, false);
        }
        modal({
            name: "confirm",
            title: "Перенести ЭТрН в архив?",
            text: "ЭТрН будет перенесена в архив Контура. Её можно будет вернуть из архива.",
            buttons: { confirm: "Перенести в архив", cancel: "Отмена" },
            action: () => operation.changeStatus(waybillId, true),
        });
    };

    return canArchive ? (
        <button
            type="button"
            className={`btn btn-xs ${archived ? "btn-outline-success" : "btn-outline-danger"}`}
            disabled={pending}
            onClick={handleClick}
        >
            {pending ? "Обновление..." : archived ? "Вернуть из архива" : "Перенести в архив"}
        </button>
    ) : null;
};

/** Show archive state even when the user cannot change it. */
export const WaybillArchiveBadge = ({ intStateFlags }: { intStateFlags: number }) =>
    isWaybillArchived(intStateFlags) ? <span className="badge badge-secondary ml-2">В архиве</span> : null;
